import { DealerLedgerViewModel, LedgerPeriod, StaffAccountsViewModel, type StaffAccount } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import React, { useState, useEffect, useMemo } from "react";
import { formatCurrency } from "../component/CarCard";
import Icons from "../component/Icons";
import { client } from "../../client";
import { resolveStaffLoginEmail } from "../../lib/staffIdentity";

const AVAILABLE_ROLES = ['admin', 'finance', 'technician', 'sales'];

const AdminEmployees: React.FC = () => {
    const accountsVm = useMemo(() => new StaffAccountsViewModel(client, { resolveEmail: resolveStaffLoginEmail }), []);
    const ledgerVm = useMemo(() => new DealerLedgerViewModel(client), []);
    const { accounts } = useViewModel(accountsVm);
    useViewModel(ledgerVm);
    const expenses = ledgerVm.payrollExpenses();
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState<StaffAccount | null>(null);
    const [resetData, setResetData] = useState<{ username: string; resetCode: string } | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        void accountsVm.load();
        void ledgerVm.loadLedger(LedgerPeriod.all());
    }, [accountsVm, ledgerVm]);

    const handleAddAccount = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        const formData = new FormData(e.currentTarget);
        const roles = Array.from(formData.getAll('roles')) as string[];

        const created = await accountsVm.create({
            username: String(formData.get('username') || ''),
            roles,
            phoneNumber: String(formData.get('phoneNumber') || ''),
        });
        if (!created) {
            setError(accountsVm.state.error || 'Failed to create account');
            return;
        }
        setResetData(created);
        setIsAddAccountModalOpen(false);
    };

    const handleUpdateAccount = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!editingAccount) return;
        setError('');
        const formData = new FormData(e.currentTarget);
        const roles = Array.from(formData.getAll('roles')) as string[];

        if (await accountsVm.update(editingAccount.id, { roles, phoneNumber: String(formData.get('phoneNumber') || '') })) {
            setEditingAccount(null);
        } else {
            setError(accountsVm.state.error || 'Failed to update account');
        }
    };

    const handleResetPassword = async (id: string) => {
        if (!window.confirm('Generate a new one-time reset code for this user?')) return;
        await accountsVm.resetPassword(id);
        setError(accountsVm.state.error || 'Failed to reset password');
    };

    const handleDeleteAccount = async (id: string) => {
        if (!window.confirm('Permanently delete this account?')) return;
        if (!(await accountsVm.remove(id))) alert(accountsVm.state.error || 'Failed to delete');
    };

    const handleLogPayment = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const ok = await ledgerVm.recordExpense({
            category: 'Payroll',
            description: `EMP: ${formData.get('employeeName')}`,
            amount: Number(formData.get('amount')),
            date: String(formData.get('date') || ''),
        });
        if (ok) {
            setIsPaymentModalOpen(false);
            await ledgerVm.loadLedger(LedgerPeriod.all());
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-gray-900 leading-none">Team & Payroll</h1>
                    <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest mt-2">Manage Staff Access and Earnings</p>
                </div>
                <div className="flex space-x-3 w-full sm:w-auto">
                    <button onClick={() => setIsAddAccountModalOpen(true)} className="flex-1 sm:flex-none bg-gray-100 text-gray-900 px-6 py-3 rounded-xl font-black uppercase shadow-sm hover:bg-gray-200 transition">
                        + Add Member
                    </button>
                    <button onClick={() => setIsPaymentModalOpen(true)} className="flex-1 sm:flex-none bg-gray-900 text-white px-6 py-3 rounded-xl font-black uppercase shadow-lg hover:bg-black transition">
                        + Log Payment
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {accounts.map((acc) => {
                    const payments = expenses.filter(exp => exp.description === `EMP: ${acc.displayName}` || exp.description === `EMP: ${acc.username}`);
                    const totalYTD = payments.reduce((sum, p) => sum + p.amount, 0);
                    return (
                        <div key={acc.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col group hover:shadow-lg transition">
                            <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-start">
                                <div>
                                    <h3 className="text-2xl font-black uppercase italic tracking-tighter text-gray-900">{acc.displayName}</h3>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">@{acc.username}</p>
                                    {acc.phoneNumber && <p className="text-[10px] font-bold text-red-600 uppercase tracking-widest mb-2">{acc.phoneNumber}</p>}
                                    <div className="flex flex-wrap gap-1">
                                        {acc.roles.map(r => (
                                            <span key={r} className="px-2 py-0.5 bg-red-50 text-red-600 text-[8px] font-black uppercase rounded-full border border-red-100">{r}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <button onClick={() => setEditingAccount(acc)} className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-full shadow-sm transition"><Icons.Menu /></button>
                                </div>
                            </div>
                            <div className="p-6 bg-white flex-grow">
                                <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Year To Date Earnings</span>
                                <span className="text-3xl font-black text-green-600 tracking-tighter">{formatCurrency(totalYTD)}</span>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between">
                                {!acc.roles.includes('owner') ? (
                                    <button onClick={() => handleResetPassword(acc.id)} className="text-[10px] font-black uppercase text-gray-500 hover:text-red-600 transition">Reset Password</button>
                                ) : (
                                    <span className="text-[10px] font-black uppercase text-gray-300">Password Locked</span>
                                )}
                                {!acc.roles.includes('owner') && (
                                    <button onClick={() => handleDeleteAccount(acc.id)} className="text-[10px] font-black uppercase text-gray-400 hover:text-red-600 transition">Delete</button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modals */}
            {isAddAccountModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[150] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsAddAccountModalOpen(false)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleAddAccount} className="p-8 space-y-6">
                            <h3 className="text-2xl font-black italic uppercase tracking-tight">New Staff Member</h3>
                            {error && <p className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl">{error}</p>}
                            <div className="space-y-4">
                                <input required name="username" type="text" placeholder="Username (unique)" className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600" />
                                <input required name="displayName" type="text" placeholder="Full Display Name" className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600" />
                                <input name="phoneNumber" type="tel" placeholder="Phone Number (e.g. 507-555-0199)" className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600" />
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Assign Roles</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {AVAILABLE_ROLES.map(role => (
                                            <label key={role} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                <input type="checkbox" name="roles" value={role} defaultChecked={role === 'sales'} className="rounded text-red-600" />
                                                <span className="text-xs font-bold uppercase">{role}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <button type="submit" className="w-full bg-gray-900 text-white font-black uppercase py-5 rounded-2xl hover:bg-black shadow-xl shadow-gray-200 transition">Create Account</button>
                        </form>
                    </div>
                </div>
            )}

            {editingAccount && (
                <div className="fixed inset-0 bg-black/60 z-[150] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setEditingAccount(null)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleUpdateAccount} className="p-8 space-y-6">
                            <h3 className="text-2xl font-black italic uppercase tracking-tight">Edit Member</h3>
                            {error && <p className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl">{error}</p>}
                            <div className="space-y-4">
                                <input disabled value={editingAccount.username} className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold bg-gray-50 text-gray-400" />
                                <input required name="displayName" type="text" defaultValue={editingAccount.displayName} placeholder="Full Display Name" className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600" />
                                <input name="phoneNumber" type="tel" defaultValue={editingAccount.phoneNumber} placeholder="Phone Number" className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600" />
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Update Roles</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {AVAILABLE_ROLES.map(role => (
                                            <label key={role} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                <input type="checkbox" name="roles" value={role} defaultChecked={editingAccount.roles.includes(role)} className="rounded text-red-600" />
                                                <span className="text-xs font-bold uppercase">{role}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <button type="submit" className="w-full bg-gray-900 text-white font-black uppercase py-5 rounded-2xl hover:bg-black shadow-xl shadow-gray-200 transition">Save Changes</button>
                        </form>
                    </div>
                </div>
            )}

            {resetData && (
                <div className="fixed inset-0 bg-gray-900/90 z-[200] flex items-center justify-center p-4 backdrop-blur-xl">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center border-4 border-amber-400">
                        <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Icons.Shield />
                        </div>
                        <h3 className="text-2xl font-black uppercase italic tracking-tight mb-2">Account Ready</h3>
                        <p className="text-sm font-bold text-gray-500 mb-8">Share this code with <span className="text-gray-900">@{resetData.username}</span> to set their password.</p>
                        <div className="bg-gray-50 p-6 rounded-2xl mb-8 border-2 border-dashed border-gray-200">
                            <span className="text-4xl font-black tracking-[0.2em] text-gray-900 uppercase font-mono">{resetData.resetCode}</span>
                        </div>
                        <button onClick={() => setResetData(null)} className="w-full bg-gray-900 text-white font-black uppercase py-4 rounded-xl">Got It, Securely Shared</button>
                    </div>
                </div>
            )}

            {isPaymentModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[150] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsPaymentModalOpen(false)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleLogPayment} className="p-8 space-y-6">
                            <h3 className="text-2xl font-black italic uppercase tracking-tight">Issue Payment</h3>
                            <select required name="employeeName" className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600 appearance-none bg-white">
                                <option value="">Select Member...</option>
                                {accounts.map(a => <option key={a.id} value={a.displayName}>{a.displayName}</option>)}
                            </select>
                            <input required name="amount" type="number" step="0.01" placeholder="Amount ($)" className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600" />
                            <input required name="date" type="date" defaultValue={new Date().toISOString().split('T')[0]} className="w-full border-2 border-gray-100 rounded-xl p-4 font-bold outline-none focus:border-red-600" />
                            <button type="submit" className="w-full bg-green-600 text-white font-black uppercase py-5 rounded-2xl hover:bg-green-700 shadow-xl shadow-green-100 transition">Log Payment</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminEmployees;
